import { useCallback, useEffect, useMemo, useRef } from "react";
import { supabase } from "./supabase";

function useSupabaseRoomSubscription({
  subscriptionId,
  channelName,
  initialPresenceState = {},
  onUpdatePresence = (arg) => {},
  onEvent = (arg) => {},
}) {
  const channel = useRef(null);
  const prevState = useRef({
    subscriptionId,
    ...initialPresenceState,
  });
  const onUpdatePresenceRef = useRef(onUpdatePresence);
  const onEventRef = useRef(onEvent);

  useEffect(() => {
    onUpdatePresenceRef.current = onUpdatePresence;
    onEventRef.current = onEvent;
  });

  useEffect(() => {
    if (!subscriptionId || !supabase || !channelName) return;

    const presenceChannel = supabase.channel(channelName, {
      config: {
        broadcast: { self: true },
        presence: { key: subscriptionId },
      },
    });

    const updatePresenceState = (callbackRef) => () => {
      const currentState = presenceChannel.presenceState();
      const stateArray = Object.values(currentState).flat();
      return callbackRef.current(stateArray);
    };

    presenceChannel
      .on(
        "presence",
        { event: "sync" },
        updatePresenceState(onUpdatePresenceRef)
      )
      .on("broadcast", { event: "*" }, (payload) => {
        onEventRef.current(payload);
      })
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          presenceChannel.track(prevState.current);
        }
      });

    channel.current = presenceChannel;

    return () => {
      if (presenceChannel) {
        presenceChannel.untrack();
        supabase.removeChannel(presenceChannel);
      }
    };
  }, [subscriptionId, channelName]);

  const updateState = useCallback(
    async (dataUpdates) => {
      if (!channel.current) {
        console.warn(`${channelName} channel not subscribed yet.`);
        return;
      }

      const updatedData = {
        ...prevState.current,
        ...dataUpdates,
      };

      prevState.current = updatedData;

      const { error } = await channel.current.track(updatedData);
      if (error) {
        console.error("Failed to update presence state:", error);
      }
    },
    [channelName]
  );

  const broadcastEvent = useCallback(
    async (event, payload) => {
      if (!channel.current) {
        console.warn(
          `${channelName} channel not subscribed yet for broadcast.`
        );
        return;
      }
      const { error } = await channel.current.send({
        type: "broadcast",
        event,
        payload: {
          senderId: subscriptionId,
          ...payload,
        },
      });
      if (error) {
        console.error("Failed to broadcast event:", error);
      }
    },
    [channelName, subscriptionId]
  );

  return useMemo(
    () => ({
      updateState,
      broadcastEvent,
    }),
    [updateState, broadcastEvent]
  );
}

export default useSupabaseRoomSubscription;
